import axios from 'axios';
import { User, ImportUserData, ImportResponse } from '../types/user';
import { API_CONFIG, getApiUrl } from '../config/apiConfig';

export const userService = {
  async getUsers(): Promise<User[]> {
    const response = await axios.get(API_CONFIG.BASE_URL);
    return response.data;
  },

  async getUserById(id: string): Promise<User> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/${id}`);
    return response.data;
  },

  async importUsers(users: ImportUserData[], username: string, role: string): Promise<ImportResponse> {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}/User/ImportUsers?username=${encodeURIComponent(username)}&role=${encodeURIComponent(role)}`,
      { users }
    );
    return response.data;
  },

  async downloadSampleTemplate(): Promise<Blob> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/sample-template`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getAllUsers: async (
    userName: string,
    userRole: string,
    page: number = 1,
    pageSize: number = 50,
    filters: { username: string; name: string; role: string; status: string } = { username: '', name: '', role: '', status: '' }
  ): Promise<{ data: User[]; totalCount: number; page: number; pageSize: number; totalPages: number }> => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/User/GetAllUsers`, {
        params: {
          userName,
          userRole,
          page,
          pageSize,
          filterUsername: filters.username || undefined,
          filterName: filters.name || undefined,
          filterRole: filters.role || undefined,
          filterStatus: filters.status || undefined
        }
      });
      // Transform the API response to match our frontend User type
      const transformedData = response.data.data.map((user: any) => ({
        id: user.id,
        username: user.username,
        name: user.name,
        role: user.role,
        status: user.isActive ? 'active' : 'inactive',
        isActive: user.isActive || false,
        sellerType: user.sellerType,
        location: user.location,
        currentBelt: user.currentBelt,
        totalPoints: user.totalPoints,
        distributorCode: user.distributorCode,
        trainerCode: user.trainerCode,
        createdAt: user.createdAt,
        modifiedAt: user.modifiedAt,
        password: user.password,
        profileImage: user.profileImage
      }));
      
      return {
        data: transformedData,
        totalCount: response.data.totalCount,
        page: response.data.page,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  updateUser: async (userId: number, updateData: { name: string; location: string; isActive: boolean; trainerCode?: string; sellerType?: string }): Promise<User> => {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/User/UpdateUser/${userId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  importTrainerSellerMapping: async (mappings: any[]): Promise<any> => {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/User/MapTrainersToSellers`, {
        mappings: mappings.map(mapping => ({
          userCode: mapping.SellerCode,
          trainerCode: mapping.TrainerCode
        }))
      });
      return response.data;
    } catch (error) {
      console.error('Error importing trainer-seller mapping:', error);
      throw error;
    }
  },

  getTrainers: async (): Promise<any[]> => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/User/GetTrainers`);
      return response.data;
    } catch (error) {
      console.error('Error fetching trainers:', error);
      throw error;
    }
  }
}; 